import { useSearchQuery } from "@search/api/useSearchQuery";
import { FilterSet } from "@search/store/FilterSet";
import { Filters } from "@search/store/types";
import clsx from "clsx";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";

import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./Pagination.module.css";

const DEFAULT_PAGE_RANGE = 5;
const DEFAULT_PAGE_MARGIN = 3;

const ref = "pagination";

interface PaginationProps extends Partial<CommonProps> {
  margin?: number;
  range?: number;
  filters: Filters;
}

function usePagination(props: PaginationProps) {
  const {
    color = ColorEnum.DARKGREEN,
    id = createId(ref),
    margin = DEFAULT_PAGE_MARGIN,
    range = DEFAULT_PAGE_RANGE,
    filters,
  } = props;
  const className = clsx(styles[ref], styles[color], props.className);
  const filterSet = new FilterSet(filters);
  const { page } = filters;

  return {
    className,
    id,
    margin,
    range,
    page,
    filterSet,
  };
}

function Pagination(props: PaginationProps) {
  const { className, filterSet, id, page, margin, range } =
    usePagination(props);
  const { pageCount } = useSearchQuery();
  const navigate = useNavigate();

  function updatePage(page: number) {
    const newQuery = filterSet.applyPage(page);

    navigate(newQuery);
  }

  return (
    <div className={className} id={id}>
      <ReactPaginate
        breakLabel={"…"}
        forcePage={page - 1}
        marginPagesDisplayed={margin}
        nextLabel={">"}
        onPageChange={({ selected }) => {
          updatePage(selected + 1);
        }}
        pageRangeDisplayed={range}
        pageCount={pageCount}
        previousLabel={"<"}
        renderOnZeroPageCount={null}
      />
    </div>
  );
}

export default Pagination;
export { Pagination, type PaginationProps };
